<template>
    <component
        :is="isWrappedWithLink ? 'dynamic-link' : 'div'"
        class="s-top-bar"
        :class="className"
        :to="linkUrl"
        target="_blank"
        :event="{ location: 'top ribbon', content: labelPrimary }"
    >
        <div class="a-container content">
            <v-clamp
                v-if="labelPrimary"
                class="label primary"
                tag="div"
                :max-lines="2"
                :autoresize="true"
                @clampchange="onClampChange($event, 'first')"
            >
                {{ labelPrimary }}
            </v-clamp>
            <v-clamp
                v-if="labelAuxiliary"
                class="label secondary"
                tag="div"
                :max-lines="2"
                :autoresize="true"
                @clampchange="onClampChange($event, 'second')"
            >
                {{ labelAuxiliary }}
            </v-clamp>
            <a-glyph
                v-if="iconName"
                :class="iconPosition"
                :name="iconName"
                size="m"
                class="icon"
            />

            <a-link
                class="cta"
                :to="linkUrl"
                target="_blank"
                :event="{ location: 'top ribbon', content: labelCta }"
                :text="labelCta"
            />
        </div>
    </component>
</template>

<script>
import VClamp from 'vue3-clamp';
import DynamicLink from '@core/components/dynamic-link/dynamic-link.vue';
import AGlyph from '@core/components/glyph/glyph.vue';
import ALink from '@core/components/link/link.vue';
import breakpoint from '@core/mixins/breakpoint.js';

export default {
    name: 'STopBar',
    components: {
        AGlyph,
        ALink,
        VClamp,
        DynamicLink,
    },
    mixins: [breakpoint],
    props: {
        labelPrimary: {
            type: String,
            default: '',
            required: false,
        },
        labelAuxiliary: {
            type: String,
            default: null,
        },
        labelCta: {
            type: String,
            default: null,
        },
        linkUrl: {
            type: String,
            default: null,
        },
        iconPosition: {
            type: String,
            default: 'start',
        },
        iconName: {
            type: String,
            default: null,
        },
        styleName: {
            type: String,
            default: 'yellow',
        },
    },
    data: () => ({
        isLabelClamped: {
            first: true,
            second: true,
        },
    }),
    computed: {
        className() {
            const firstLabelClamped = this.labelPrimary && this.isLabelClamped.first;
            const secondLabelClamped = this.labelAuxiliary && this.isLabelClamped.second;
            return {
                [`${this.styleName}`]: true,
                'is-link': this.isWrappedWithLink,
                'is-clamped': firstLabelClamped || secondLabelClamped,
            };
        },
        isWrappedWithLink() {
            return !this.labelCta;
        },
    },
    mounted() {
        document.addEventListener('click', this.clickHandler);
    },
    beforeUnmount() {
        document.removeEventListener('click', this.clickHandler);
    },
    methods: {
        onClampChange(clamped, label) {
            this.isLabelClamped[label] = clamped;
        },
    },
};
</script>

<style lang="postcss" scoped>
.s-top-bar {
    position: relative;
    z-index: 401; /* to be above s-main-header */
    display: flex;
    justify-content: center;
    align-items: center;
    width: 100%;
    background-size: cover;
    background-position: center;
    background-repeat: no-repeat;
    transition: background-color linear 0.2s;
    min-height: 56px;
    flex-shrink: 0;

    @media (--viewport-tablet) {
        height: 56px;
    }

    &.yellow {
        background: var(--av-fixed-warning-light);

        .content {
            color: var(--av-nav-primary);
        }

        .cta-icon {
            color: var(--av-link-primary);
            fill: var(--av-link-primary);
        }
    }

    &.blue,
    &.navy {
        background: var(--av-nav-secondary);

        .content {
            color: var(--av-inversed-primary);
        }

        .cta-icon {
            color: var(--av-link-inversed);
            fill: var(--av-link-inversed);
        }

        .icon {
            fill: var(--av-inversed-primary);
        }

        .menu-label {
            color: var(--av-inversed-primary);
            .a-glyph {
                fill: var(--av-inversed-primary);
            }
        }

        .cta:deep(.a-link__content) {
            color: var(--av-link-inversed);
        }

        .cta:hover:deep(.a-link__content),
        .menu-label:hover {
            color: var(--av-link-inversed-hover);
            .a-glyph {
                fill: var(--av-link-inversed-hover);
            }
        }

        .cta:active:deep(.a-link__content),
        .menu-label:active,
        .menu-label.active {
            color: var(--av-link-inversed-active);
            .a-glyph {
                fill: var(--av-link-inversed-active);
            }
        }

        .cta:focus-visible:deep(.a-link__content),
        .menu-label:focus-visible {
            color: var(--av-link-inversed);

            .a-glyph {
                fill: var(--av-link-inversed);
            }
        }
    }

    &.blue {
        .cta:hover:deep(.a-link__content) {
            color: #7ab2ff;
            .a-glyph {
                fill: #7ab2ff;
            }
        }
        .menu-label:hover {
            color: var(--av-link-inversed-active);
            .a-glyph {
                fill: var(--av-link-inversed-active);
            }
        }

        .cta:focus-visible:deep(.a-link__content),
        .menu-label:focus-visible {
            color: var(--av-inversed-primary);
            background: var(--av-link-inversed-light);

            .a-glyph {
                fill: var(--av-inversed-primary);
            }
        }
    }

    &.navy {
        background: var(--av-nav-primary);
    }

    &.light-green,
    &.green {
        background: var(--av-fixed-success-light);
    }

    &.light-blue {
        background: var(--av-fixed-info-light);
    }

    &.white {
        background: var(--av-inversed-primary);
    }

    &.is-clamped {
        .label {
            flex: 1 0 0;
        }
    }

    &.is-link {
        .content {
            color: var(--av-link-primary);
        }

        &:hover {
            .content {
                color: var(--av-link-primary-hover);
            }
        }

        &.blue,
        &.navy {
            .content {
                color: var(--av-link-inversed);
            }

            &:hover {
                .content {
                    color: var(--av-link-inversed-hover);
                }
            }
        }

        &.blue:hover {
            .content {
                color: #7ab2ff;
            }
        }
    }
}

.content {
    @mixin caption-accent;
    justify-content: flex-start;
    align-items: flex-start;
    width: 100%;
    text-decoration: none;
    color: var(--av-nav-primary);
    transition: color linear 0.2s;
    column-gap: 16px;
    padding: 8px 16px;

    @media (--viewport-mobile-wide) {
        padding: 0 32px;
        flex-direction: row;
        align-items: center;
        display: flex;
        justify-content: center;
    }

    @media (--viewport-tablet) {
        font-size: 14px;
    }

    @media (--viewport-desktop-wide) {
        padding-inline: 64px;
    }

    @media (--viewport-desktop-large) {
        padding-inline: 0;
    }
}

.label {
    margin-inline-end: 2px;

    &.primary {
        order: 2;
        margin-inline-start: 0;
    }

    &.secondary {
        order: 4;
    }
}

.cta {
    transition: color linear 0.2s;
    align-items: center;
    order: 6;
    color: var(--av-link-primary);
    margin-top: 8px;

    &:hover {
        color: var(--av-link-secondary);
    }

    &:focus-visible {
        background: var(--av-brand-secondary-light);
        color: var(--av-link-primary);

        .a-glyph {
            fill: var(--av-link-primary);
        }
    }

    &:active,
    &.active {
        :deep(.a-link__content) {
            color: var(--link-secondary-active);
        }

        .a-glyph {
            fill: var(--link-secondary-active);
        }
    }

    @media (--viewport-mobile-wide) {
        display: flex;
        margin-top: 0;
    }

    @media (--viewport-tablet) {
        font-size: 14px;
    }

    :deep(.a-link__content) {
        @mixin caption-accent;
        background: transparent;

        @media (--viewport-tablet) {
            @mixin body-accent;
        }
    }
}

.cta-icon {
    margin-inline-start: 8px;
    color: var(--av-nav-primary);
    fill: var(--av-nav-primary);
}

.icon {
    fill: var(--av-nav-primary);
    display: none;
    flex-shrink: 0;
    margin: 0;

    @media (--viewport-tablet) {
        display: block;
    }

    &.start {
        order: 1;
    }

    &.divider {
        order: 3;
    }

    &.end {
        margin-inline-end: 0;
        order: 5;
    }
}
</style>
